import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    const userData = await authService.login(credentials);
    // When 2FA is enabled, login may return a challenge instead of a user.
    if (userData?.token) setUser(userData);
    return userData;
  };

  const loginWithTwoFactor = async ({ twoFactorToken, otp }) => {
    const userData = await authService.loginWithTwoFactor({ twoFactorToken, otp });
    if (userData?.token) setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const newUser = await authService.register(userData);
    if (newUser?.token) setUser(newUser);
    return newUser;
  };

  const setupTwoFactor = async () => {
    return await authService.setupTwoFactor();
  };

  const verifyTwoFactor = async ({ otp }) => {
    return await authService.verifyTwoFactor({ otp });
  };

  // Login with token (for OAuth callback)
  const loginWithToken = async (token) => {
    localStorage.setItem('token', token);
    
    // Fetch user data
    const response = await api.get('/auth/me');
    const userData = response.data;
    
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  // Initiate Google OAuth login
  const loginWithGoogle = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    window.location.href = `${apiUrl}/auth/google`;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    login,
    loginWithTwoFactor,
    register,
    logout,
    loginWithToken,
    loginWithGoogle,
    refreshUser,
    setupTwoFactor,
    verifyTwoFactor,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
